import { FormControl, FormErrorMessage, FormLabel, Select, VStack } from '@chakra-ui/react';
import { skipToken } from '@tanstack/react-query';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { NewContactForm, type NewContactFormHandle } from './NewContactForm';
import { emptyNewContact, noContactChoice, type EmployerContactChoice } from './contactChoice';
import { testIds } from './testIds';

const ADD_NEW_CONTACT = '__new__';

function selectValue(choice: EmployerContactChoice): string {
  switch (choice.mode) {
    case 'none':
      return '';
    case 'existing':
      return choice.contactId;
    case 'new':
      return ADD_NEW_CONTACT;
  }
}

function choiceForSelectValue(value: string): EmployerContactChoice {
  if (value === '') {
    return noContactChoice;
  }
  if (value === ADD_NEW_CONTACT) {
    return { mode: 'new', contact: emptyNewContact };
  }
  return { mode: 'existing', contactId: value };
}

export function EmployerContactSelect({
  employerId,
  value,
  onChange,
  newContactRef,
  isRequired = false,
  errorMessage,
}: {
  employerId: string | null;
  value: EmployerContactChoice;
  onChange: (next: EmployerContactChoice) => void;
  newContactRef?: React.Ref<NewContactFormHandle>;
  isRequired?: boolean;
  errorMessage?: string;
}): React.ReactElement {
  const { data, isLoading, isError } =
    trpcAdmin.workPlacements.employers.getEmployerDetail.useQuery(
      employerId != null ? { employerId } : skipToken,
    );
  const contacts = data?.contacts ?? [];
  const awaitingEmployer = employerId == null;

  const placeholder = awaitingEmployer
    ? t`Choose an employer first`
    : isLoading
      ? t`Loading contacts…`
      : isRequired
        ? t`Choose a contact`
        : t`No contact`;

  return (
    <VStack align="stretch" gap={4} w="full">
      <FormControl isRequired={isRequired} isInvalid={isError || errorMessage != null}>
        <FormLabel>
          <Trans>Contact</Trans>
        </FormLabel>
        <Select
          data-testid={testIds.contactSelect}
          placeholder={placeholder}
          isDisabled={awaitingEmployer || isLoading}
          value={selectValue(value)}
          onChange={(event) => onChange(choiceForSelectValue(event.target.value))}
        >
          {contacts.map((contact) => (
            <option key={contact.id} value={contact.id}>
              {`${contact.firstName} ${contact.lastName}`}
            </option>
          ))}
          {awaitingEmployer ? null : <option value={ADD_NEW_CONTACT}>{t`Add new contact`}</option>}
        </Select>
        {isError ? (
          <FormErrorMessage>
            <Trans>Unable to load contacts.</Trans>
          </FormErrorMessage>
        ) : errorMessage != null ? (
          <FormErrorMessage>{errorMessage}</FormErrorMessage>
        ) : null}
      </FormControl>

      {value.mode === 'new' ? (
        <NewContactForm
          ref={newContactRef}
          value={value.contact}
          onChange={(contact) => onChange({ mode: 'new', contact })}
        />
      ) : null}
    </VStack>
  );
}
